import { AbfLoanExpanded, BsMetaUtil, useOraclePrices } from "@bridgesplit/abf-react";
import { formatTokenAmount, formatUsd, Result } from "@bridgesplit/utils";
import { Column, OutlinedCard, Row, TagTextAlert, Text, useAppPalette } from "@bridgesplit/ui";
import { TokenListMetadata, LoopWindType } from "@bridgesplit/abf-sdk";
import { TEST_IDS } from "app/constants";
import { TransactionTypes } from "app/types";

import { TokenInputWithPrice, BalanceDisplay, AppButton, MarketHoursRepaymentAlert, TokenImage } from "../../../common";
import RepayStats from "./RepayStats";

interface RepaymentComponentProps {
    inputValue: number | undefined;
    inputMetadata: TokenListMetadata | undefined;
    onInputChange: (amt: number | undefined) => void;
    disabledInput: boolean;
    topLevelTotalPrincipalDue: number | undefined;
    repaymentAmount: number | undefined;
    loading: boolean;
    balance: number;
    balanceMetadata: TokenListMetadata | undefined;
    balanceLoading: boolean;
    balanceError: boolean;
    error: string | null;
    ctaError: string | null;
    onRepay: () => Promise<Result<unknown>>;
    onSetMax: () => void;
    showMarketHours: boolean;
    loanExpanded: AbfLoanExpanded | undefined;
    principalMetadata: TokenListMetadata | undefined;
    totalDueInBalanceDisplay: number;
    repayType: LoopWindType;
    repayLabel: string;
}

export default function RepaymentComponent({
    inputValue,
    inputMetadata,
    onInputChange,
    disabledInput,
    topLevelTotalPrincipalDue,
    repaymentAmount,
    loading,
    balance,
    balanceMetadata,
    balanceLoading,
    balanceError,
    error,
    ctaError,
    onRepay,
    onSetMax,
    showMarketHours,
    loanExpanded,
    principalMetadata,
    totalDueInBalanceDisplay,
    repayType,
    repayLabel
}: RepaymentComponentProps) {
    const { primaryToAlpha } = useAppPalette();

    const { getOracle } = useOraclePrices([principalMetadata?.mint]);

    const totalDueUsd = getOracle(principalMetadata?.mint)?.getUsdAmount(
        topLevelTotalPrincipalDue,
        principalMetadata?.decimals
    );

    const isCollateralRepay = repayType === LoopWindType.Collateral;
    const showTotalDueBalance = !balanceError && !!balance;

    return (
        <>
            <Row spacing={1}>
                <OutlinedCard padding={1.5} sx={{ pl: 1, width: "100%" }} hideBorder backgroundColor={primaryToAlpha}>
                    <Column spacing={0.5}>
                        <Row spacing={0.5}>
                            <Text color="caption" variant="body2" translate="no">
                                Total due
                            </Text>
                        </Row>
                        <Row spacing={1}>
                            <TokenImage size="md" metadata={principalMetadata} />
                            <Text variant="body1" fontWeight="semibold" translate="no">
                                {formatTokenAmount(topLevelTotalPrincipalDue, {
                                    symbol: BsMetaUtil.getSymbol(principalMetadata),
                                    hideSymbol: true,
                                    compactLargeNumbers: true
                                })}
                            </Text>

                            <Text variant="body1" color="caption" translate="no">
                                {formatUsd(totalDueUsd)}
                            </Text>
                        </Row>
                    </Column>
                </OutlinedCard>
            </Row>

            <TokenInputWithPrice
                value={inputValue}
                metadata={inputMetadata}
                selectToken={null}
                setValue={onInputChange}
                disabled={disabledInput}
                label={repayLabel}
                hideUsdPrice={isCollateralRepay}
                belowInput={
                    showTotalDueBalance && (
                        <BalanceDisplay
                            isLoading={balanceLoading}
                            balance={totalDueInBalanceDisplay}
                            metadata={balanceMetadata}
                            setMax={onSetMax}
                            insufficientBalance={false} // hardcoded to false because we show insufficient balance in cta
                            maxText="Total due"
                            hideRefresh
                            maxButtonCta="Full balance"
                        />
                    )
                }
            />

            <RepayStats loanExpanded={loanExpanded} repaymentAmount={repaymentAmount} loading={loading} />

            <Column spacing={0.5}>
                {!error && (
                    <AppButton
                        dataTestId={TEST_IDS.REPAY_BUTTON}
                        isTransaction
                        transactionType={TransactionTypes.Repay}
                        disabled={!!ctaError || !inputValue || !repaymentAmount || loading}
                        asyncCta={{ onClickWithResult: onRepay, closeDialog: "onSuccess" }}
                        fullWidth
                    >
                        {ctaError || "Make Repayment"}
                    </AppButton>
                )}

                {!!error && (
                    <TagTextAlert color="warning" textSx={{ display: "inline-block" }} icon="warning">
                        {error}
                    </TagTextAlert>
                )}

                <BalanceDisplay
                    balance={balance}
                    metadata={balanceMetadata}
                    insufficientBalance={false}
                    isLoading={balanceLoading}
                    hideRefresh={isCollateralRepay}
                    maxText="Balance"
                />
            </Column>

            {showMarketHours && <MarketHoursRepaymentAlert />}
        </>
    );
}
